import { useState, useCallback, useEffect } from "react";
import "./App.css";
import Screen1Selector from "./screens/Screen1Selector";
import Screen2Questionnaire from "./screens/Screen2Questionnaire";
import Screen3Results from "./screens/Screen3Results";
import DiagnosticFlow from "./screens/DiagnosticFlow";
import AdminPanel from "./screens/AdminPanel";
import PerfilSetup from "./screens/PerfilSetup";
import QuickStart from "./screens/QuickStart";
import MenuEngineeringResult from "./screens/MenuEngineeringResult";
import MisVentas from "./screens/MisVentas";
import CompetenciaMap from "./screens/CompetenciaMap";
import Footer from "./components/Footer";
import { Toaster } from "sonner";
import { trackVisit, markAdminSession } from "./lib/analytics";
import { readPerfil, writePerfil } from "./lib/perfil";
import { resetAccionSessionCache } from "./components/AccionDeHoy";

const STORAGE_KEY = "promptlymx:state:v1";

function readSavedState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Basic shape validation — ignore anything malformed
    if (typeof parsed !== "object" || parsed === null) return null;
    return {
      step: [1, 2, 3].includes(parsed.step) ? parsed.step : 1,
      businessType: typeof parsed.businessType === "string" ? parsed.businessType : null,
      otherText: typeof parsed.otherText === "string" ? parsed.otherText : "",
      ownerName: typeof parsed.ownerName === "string" ? parsed.ownerName : "",
      city: typeof parsed.city === "string" ? parsed.city : "",
      goal: typeof parsed.goal === "string" ? parsed.goal : "",
      answers: typeof parsed.answers === "object" && parsed.answers !== null ? parsed.answers : {},
      qStep: Number.isInteger(parsed.qStep) && parsed.qStep >= 0 ? parsed.qStep : 0,
    };
  } catch {
    return null;
  }
}

// Synchronous merge-write to localStorage. Reads whatever is there, applies the patch,
// and writes back. Used in every navigation handler BEFORE setState — so even if React
// has any glitch (StrictMode quirk, low-memory remount on iOS Safari, mid-handler throw,
// gesture cancel), the next render/reload will read the correct step from localStorage
// and the user lands on the right screen.
function writeSavedState(patch) {
  try {
    let current = {};
    try {
      current = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") || {};
    } catch {
      current = {};
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...current, ...patch }));
  } catch {
    // Safari private mode / quota — ignore. In-memory React state still drives the UI.
  }
}

// Cross-browser scroll-to-top. We avoid `behavior: "instant"` because it throws a
// TypeError on iOS Safari < 16.3 and on some Android WebViews — and a throw inside the
// navigation handler is exactly what was leaving users stranded on Screen 1 after
// finishing the questionnaire. Plain coordinate form is universally supported.
function scrollTopSafe() {
  try {
    window.scrollTo(0, 0);
  } catch {
    // ignore — irrelevant for navigation correctness
  }
}

const DEFAULT_STATE = {
  step: 1,
  businessType: null,
  otherText: "",
  ownerName: "",
  city: "",
  goal: "",
  answers: {},
  qStep: 0,
};

function App() {
  const initial = readSavedState() || DEFAULT_STATE;
  const [step, setStep] = useState(initial.step);
  const [businessType, setBusinessType] = useState(initial.businessType);
  const [otherText, setOtherText] = useState(initial.otherText);
  const [ownerName, setOwnerName] = useState(initial.ownerName);
  const [city, setCity] = useState(initial.city);
  const [goal, setGoal] = useState(initial.goal);
  const [answers, setAnswers] = useState(initial.answers);
  const [qStep, setQStep] = useState(initial.qStep);

  // Persistent business profile (localStorage: "negoia_perfil"). Its presence
  // switches the landing view from PerfilSetup (first-time users) to QuickStart.
  const [perfil, setPerfil] = useState(() => readPerfil());

  // Independent view toggle:
  //   "root"            → PerfilSetup (no perfil) OR QuickStart (has perfil)
  //   "main"            → existing Screen1/2/3 flow
  //   "diagnostic"      → standalone 12-question business diagnostic
  //   "menu-engineering"→ generated Menu Engineering master prompt
  //   "edit-perfil"     → PerfilSetup opened from QuickStart in edit mode
  //
  // Initial view is derived from saved state so a reload during Screen 2/3
  // restores the user's generated results — otherwise perfil users would be
  // dumped back to QuickStart on refresh and lose their prompts.
  const [view, setView] = useState(() => (initial.step > 1 ? "main" : "root"));

  // Admin panel gate: only render when the URL contains ?admin=1 (or =true).
  const isAdmin = (() => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search);
    const v = params.get("admin");
    return v === "1" || v === "true";
  })();

  // Analytics: fire exactly one visit per browser session. On the admin URL we
  // instead flag the session as admin so its events are excluded from the
  // analytics counts.
  useEffect(() => {
    if (isAdmin) markAdminSession();
    else trackVisit();
  }, [isAdmin]);

  // Background safety net: persist every state change too.
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ step, businessType, otherText, ownerName, city, goal, answers, qStep }),
      );
    } catch {
      // ignore
    }
  }, [step, businessType, otherText, ownerName, city, goal, answers, qStep]);

  const handleStart = useCallback(
    ({ businessType: bt, otherText: ot, ownerName: on, city: c, goal: g }) => {
      const businessChanged = businessType !== bt;
      writeSavedState({
        step: 2,
        businessType: bt,
        otherText: ot,
        ownerName: on,
        city: c,
        goal: g,
        ...(businessChanged ? { answers: {}, qStep: 0 } : {}),
      });
      setBusinessType(bt);
      if (businessChanged) {
        setAnswers({});
        setQStep(0);
      }
      setOtherText(ot);
      setOwnerName(on);
      setCity(c);
      setGoal(g);
      setStep(2);
      scrollTopSafe();
    },
    [businessType],
  );

  const handleBackToStart = useCallback(() => {
    // With a saved perfil, "back" from the questionnaire returns to QuickStart
    // (the natural entry point), otherwise to Screen 1.
    if (perfil) {
      writeSavedState({ step: 1 });
      setStep(1);
      setView("root");
      scrollTopSafe();
      return;
    }
    writeSavedState({ step: 1 });
    setStep(1);
    scrollTopSafe();
  }, [perfil]);

  const handleFinishQuestions = useCallback((ans) => {
    writeSavedState({ step: 3, answers: ans });
    setAnswers(ans);
    setStep(3);
    scrollTopSafe();
  }, []);

  const handleRestart = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    // Clear the per-session cache of "Acción de hoy" picks so the next flow
    // rotates to a fresh variant (spec: never show the same recomendation
    // twice in a row).
    resetAccionSessionCache();
    setBusinessType(null);
    setOtherText("");
    setOwnerName("");
    setCity("");
    setGoal("");
    setAnswers({});
    setQStep(0);
    setStep(1);
    // If the user has a perfil, return them to their QuickStart hub;
    // otherwise fall back to Screen 1.
    if (perfil) setView("root");
    scrollTopSafe();
  }, [perfil]);

  // Perfil handlers ─────────────────────────────────────────────────────────
  const handlePerfilSave = useCallback((newPerfil) => {
    writePerfil(newPerfil);
    setPerfil(newPerfil);
    setView("root");
    scrollTopSafe();
  }, []);

  const handleQuickPublication = useCallback(() => {
    if (!perfil) return;
    // Skip Screen 1 — the button explicitly says "necesito una publicación",
    // so we pre-fill the profile data + set goal = "Publicar en redes sociales"
    // and jump directly into the adaptive questionnaire.
    const changed = businessType !== perfil.giro;
    writeSavedState({
      step: 2,
      businessType: perfil.giro,
      city: perfil.ciudad,
      ownerName: perfil.nombreNegocio,
      otherText: "",
      goal: "Publicar en redes sociales",
      ...(changed ? { answers: {}, qStep: 0 } : {}),
    });
    setBusinessType(perfil.giro);
    setCity(perfil.ciudad);
    setOwnerName(perfil.nombreNegocio);
    setOtherText("");
    setGoal("Publicar en redes sociales");
    if (changed) {
      setAnswers({});
      setQStep(0);
    }
    setStep(2);
    setView("main");
    scrollTopSafe();
  }, [perfil, businessType]);

  const handleFullFlow = useCallback(() => {
    if (!perfil) return;
    // Open the complete existing flow with profile data prefilled on Screen 1
    // (user can still change giro/goal/city before starting the questionnaire).
    writeSavedState({
      step: 1,
      businessType: perfil.giro,
      city: perfil.ciudad,
      ownerName: perfil.nombreNegocio,
      goal: "",
      otherText: "",
    });
    setBusinessType(perfil.giro);
    setCity(perfil.ciudad);
    setOwnerName(perfil.nombreNegocio);
    setGoal("");
    setOtherText("");
    setStep(1);
    setView("main");
    scrollTopSafe();
  }, [perfil]);

  const handleMenuEngineering = useCallback(() => {
    if (!perfil) return;
    setView("menu-engineering");
    scrollTopSafe();
  }, [perfil]);

  const handleMisVentas = useCallback(() => {
    setView("ventas");
    scrollTopSafe();
  }, []);

  const handleCompetencia = useCallback(() => {
    setView("competencia");
    scrollTopSafe();
  }, []);

  const handleEditPerfil = useCallback(() => {
    setView("edit-perfil");
    scrollTopSafe();
  }, []);

  const handleBackToRoot = useCallback(() => {
    setView("root");
    scrollTopSafe();
  }, []);

  // Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="App min-h-[100dvh] bg-slate-100">
      <div
        className="max-w-md mx-auto min-h-[100dvh] bg-white sm:shadow-2xl sm:border-x sm:border-slate-100 relative"
        data-testid="app-shell"
      >
        {isAdmin ? (
          <AdminPanel />
        ) : view === "diagnostic" ? (
          <DiagnosticFlow
            onExit={() => setView("root")}
            businessType={businessType}
            otherText={otherText}
          />
        ) : view === "menu-engineering" && perfil ? (
          <MenuEngineeringResult perfil={perfil} onBack={handleBackToRoot} />
        ) : view === "ventas" ? (
          <MisVentas onBack={handleBackToRoot} />
        ) : view === "competencia" && perfil ? (
          <CompetenciaMap
            perfil={perfil}
            onBack={handleBackToRoot}
            onGenerateStrategy={handleMenuEngineering}
          />
        ) : view === "edit-perfil" ? (
          <PerfilSetup
            initialPerfil={perfil}
            onSave={handlePerfilSave}
            onCancel={handleBackToRoot}
          />
        ) : view === "root" && !perfil ? (
          <PerfilSetup onSave={handlePerfilSave} />
        ) : view === "root" && perfil ? (
          <QuickStart
            perfil={perfil}
            onMenuEngineering={handleMenuEngineering}
            onQuickPublication={handleQuickPublication}
            onFullFlow={handleFullFlow}
            onEditPerfil={handleEditPerfil}
            onMisVentas={handleMisVentas}
            onCompetencia={handleCompetencia}
          />
        ) : (
          <>
            {step === 1 && (
              <Screen1Selector
                initialBusinessType={businessType}
                initialOtherText={otherText}
                initialOwnerName={ownerName}
                initialCity={city}
                initialGoal={goal}
                onStart={handleStart}
                onStartDiagnostic={() => setView("diagnostic")}
                onBack={perfil ? handleBackToRoot : undefined}
              />
            )}
            {step === 2 && (
              <Screen2Questionnaire
                businessType={businessType}
                answers={answers}
                onAnswersChange={setAnswers}
                qStep={qStep}
                onQStepChange={setQStep}
                onBack={handleBackToStart}
                onFinish={handleFinishQuestions}
              />
            )}
            {step === 3 && (
              <Screen3Results
                businessType={businessType}
                answers={answers}
                city={city}
                ownerName={ownerName}
                otherText={otherText}
                goal={goal}
                onRestart={handleRestart}
              />
            )}
          </>
        )}
        <Footer />
      </div>
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            fontFamily: "Poppins, sans-serif",
            fontWeight: 600,
          },
        }}
      />
    </div>
  );
}

export default App;
